import { QueryOptions } from '@apollo/client'
import statesUs from 'states-us'
import {
  AccountCategory,
  BillingCycle,
  BillStatus,
  BillTimelineAction,
  CaseStatus,
  ExternalUserCaseRole,
  PaymentMethod,
  TransactionStatus,
  UserRole,
  UserType
} from '../../generated'
import { CaseTitle, CreateCaseCustomInput, LoginNotice, UpdateCaseCustomProps } from '../interfaceTypes'

// Apollo messages
export const INVALID_OR_EXPIRED_TOKEN_MESSAGE = 'Sorry! Your token is expired or invalid'
export const NOT_FOUND_EXCEPTION = 'Not Found Exception'
export const CONFLICT_EXCEPTION = 'Conflict Exception'
export const SOMETHING_WENT_WRONG = 'Something went wrong'
export const CASE_HAS_BEEN_CLOSED_UNABLE_TO_UPDATE = 'Case has been closed unable to update'
export const ALREADY_BILLED_AND_CANNOT_BE_CHANGED = 'is already billed and cannot be changed'
// Phrase in both API Initial Payment lock messages (initialPaymentVoidFirstMessage, initialPaymentLockedMessage).
export const INITIAL_PAYMENT_LOCK_PHRASE = 'this Initial Payment'
export const REQUEST_NOT_FOUND = 'Requests not found for current user'
export const INVALID_AUTH_TOKEN = 'Invalid Authorization Token - Expired or Invalid'
export const TOKEN_NOT_FOUND = 'Token not found'
export const UNAUTHENTICATED = 'UNAUTHENTICATED'
export const AUTH_TOKEN = 'helmer_admin_token'
export const USER_EMAIL = 'helmer_user_email'
export const PRECONDITION_FAILED_EXCEPTION = 'Precondition Failed Exception'
export const UNAUTHORIZED = 'Unauthorized'

// Collection attorney IDs for print job filtering
export const COLL1_ATTORNEY_ID = 'f515000c-de66-4ee3-ada0-8c63ab0b7170'
export const COLL2_ATTORNEY_ID = '4aa60f31-85d9-4e41-8972-a601d0b31919'
export const TOKEN_INVALID = 'Token Invalid'
export const FORGET_PASSWORD = 'Forgot password?'
export const BAD_USER_INPUT = 'BAD_USER_INPUT'
export const LOGIN = 'Login'
export const SIGN_UP = 'Sign Up'
export const LOG_IN = 'Log In'
export const INVALID_EMAIL = 'Invalid email address'
export const GET_STARTED = 'Get started for free'
export const SAVE = 'Save'
export const USER_ALREADY_EXIST = 'User already exists'
export const CREATE_USER_SUCCESS = 'User created successfully'
export const RESET_PASSWORD = 'Reset Password'
export const FORGET_PASSWORD_INSTRUCTION = 'Please enter your associated email to reset password.'
export const SEND_INSTRUCTION = 'Send instructions'
export const BACK_TO_LOGIN = 'Back to login page'
export const SET_PASSWORD = 'Set your Password'
export const SET_PASSWORD_SUBTITLE = 'Set up password to complete your signup.'
export const FORBIDDEN_EXCEPTION = 'forbidden exception'
export const EMAIL_CHANGED_OR_NOT_VERIFIED_MESSAGE = 'Email changed or not verified, please verify your email'
export const WRONG_EMAIL_OR_PASSWORD = 'Incorrect email or password.'
// Change password
export const CHANGE_PASSWORD = 'Change Password'
export const CURRENT_PASSWORD = 'Current Password'
export const NEW_PASSWORD = 'New Password'
export const CONFIRM_NEW_PASSWORD = 'Confirm New Password'
export const CHANGE_PASSWORD_DESCRIPTION =
  'Choose a new password for signing in. You will stay signed in on this device.'
export const CHANGE_PASSWORD_SUCCESS = 'Your password has been changed'
export const PASSWORD_MUST_DIFFER = 'Your new password must be different from your current password'
export const CURRENT_PASSWORD_INCORRECT = 'Your current password is incorrect'

export const INVALID_CURRENT_PASSWORD = 'INVALID_CURRENT_PASSWORD'
export const PASSWORD_TOO_WEAK = 'PASSWORD_TOO_WEAK'
export const PASSWORD_UNCHANGED = 'PASSWORD_UNCHANGED'

// The global Apollo error link already toasts the explanation the API sends with these,
// so the card must not raise a second one. It matches on the code purely to decide where
// to put the field error.
export const CHANGE_PASSWORD_ERROR_CODES = [INVALID_CURRENT_PASSWORD, PASSWORD_TOO_WEAK, PASSWORD_UNCHANGED]
// Multi-factor authentication
// The API answers an MFA-protected login with 202 instead of 200 and withholds the
// access token, so the status is what tells the login form to ask for a code.
export const MFA_REQUIRED_STATUS = 202
export const INVALID_OTP = 'INVALID_OTP'
export const OTP_EXPIRED = 'OTP_EXPIRED'
export const OTP_ATTEMPTS_EXCEEDED = 'OTP_ATTEMPTS_EXCEEDED'
export const INVALID_MFA_TOKEN = 'INVALID_MFA_TOKEN'
export const MFA_TOKEN_EXPIRED = 'MFA_TOKEN_EXPIRED'
export const MFA_METHOD_NOT_SUPPORTED = 'MFA_METHOD_NOT_SUPPORTED'
export const MFA_AUTH_NOT_ENROLLED = 'MFA_AUTH_NOT_ENROLLED'
export const MFA_METHOD_NOT_ENABLED = 'MFA_METHOD_NOT_ENABLED'
export const MFA_ENROLLMENT_EXPIRED = 'MFA_ENROLLMENT_EXPIRED'
export const MFA_AUTH_ENABLE_NOT_PERMITTED = 'MFA_AUTH_ENABLE_NOT_PERMITTED'
// Too many wrong passwords or codes. The lock is permanent until an admin lifts it, and
// the API's own message ("contact support") is what the user sees.
export const ACCOUNT_LOCKED = 'ACCOUNT_LOCKED'
// Shown on the sign-in widget itself rather than toasted, so it stays until the next try.
export const ACCOUNT_LOCKED_MESSAGE = 'Your account is locked due to invalid login attempts. Please contact support.'
// `remaining` is what the API reports as still left (max minus failed attempts), so it is shown as is.
export const remainingLoginAttemptsMessage = (remaining: number): string =>
  remaining === 1
    ? 'You have 1 attempt remaining. Your account will be locked if the next attempt fails.'
    : `You have ${remaining} attempts remaining before your account is locked.`
export const ACCOUNT_LOCKED_NOTICE: LoginNotice = { severity: 'error', message: ACCOUNT_LOCKED_MESSAGE }

// Shown on the sign-in widget when it was reached because the session ended.
export const SESSION_EXPIRED_MESSAGE = 'Your session has expired. Please log in again.'
export const SESSION_INVALID_MESSAGE = 'Your session is no longer valid. Please log in again.'
export const SIGNED_OUT_MESSAGE = 'You have been signed out. Please log in again.'
export const sessionEndingMessage = (minutes: number) =>
  `Your session will end in ${minutes === 1 ? '1 minute' : `${minutes} minutes`}. ` +
  'Save your work now; you will need to log in again to continue.'
export const TIMER_PAUSED_FOR_SESSION_END = 'Your running timer has been paused because your session is ending.'

// The global Apollo error link already toasts the explanation the API sends with
// these, so components must not raise a second one. They match on the code purely to
// decide what the form should do next.
export const MFA_ERROR_CODES = [
  INVALID_OTP,
  OTP_EXPIRED,
  OTP_ATTEMPTS_EXCEEDED,
  INVALID_MFA_TOKEN,
  MFA_TOKEN_EXPIRED,
  MFA_METHOD_NOT_SUPPORTED,
  MFA_AUTH_NOT_ENROLLED,
  MFA_METHOD_NOT_ENABLED,
  MFA_ENROLLMENT_EXPIRED,
  MFA_AUTH_ENABLE_NOT_PERMITTED,
  ACCOUNT_LOCKED
]

// Errors that invalidate the challenge itself — the user has to start over rather
// than retype the code.
export const MFA_SESSION_ENDED_ERRORS = [OTP_ATTEMPTS_EXCEEDED, INVALID_MFA_TOKEN, MFA_TOKEN_EXPIRED, ACCOUNT_LOCKED]

export const TWO_FACTOR_AUTHENTICATION = 'Two-Factor Authentication'
export const VERIFY = 'Verify'
export const RESEND_CODE = 'Resend code'
export const BACK_TO_SIGN_IN = 'Back to Sign In'
export const MFA_EMAIL_LABEL = 'Email verification code'
export const MFA_EMAIL_DESCRIPTION = 'Receive a one-time code by email each time you sign in.'
export const MFA_AUTH_LABEL = 'Authenticator app'
export const MFA_AUTH_DESCRIPTION = 'Verify with a code from an authenticator app such as Google Authenticator.'
export const MFA_ENABLED_SUCCESS = 'Two-factor authentication enabled'
export const MFA_DISABLED_SUCCESS = 'Two-factor authentication disabled'
export const MFA_OTP_SENT = 'We have sent a verification code to your email'
export const MFA_PRIORITY_NOTE =
  'When both are on, the emailed code is used at sign in and the authenticator stays as backup.'
export const SET_UP_AUTHENTICATOR = 'Set up authenticator app'
export const AUTHENTICATOR_SCAN_INSTRUCTION =
  'Scan this with your authenticator app, then enter the 6-digit code it shows.'
export const AUTHENTICATOR_MANUAL_ENTRY = 'Or enter this key manually:'
export const AUTHENTICATOR_ENABLED_SUCCESS = 'Authenticator app enabled'
export const CONFIRM = 'Confirm'
export const AUTHENTICATOR_CODE_PROMPT = 'Enter the 6-digit code from your authenticator app.'
export const USE_AUTHENTICATOR_INSTEAD = 'Use authenticator app instead'
export const USE_EMAIL_INSTEAD = 'Email me a code instead'
export const MFA_SWITCHED_TO_EMAIL = 'We have sent a verification code to your email'
export const MFA_SWITCHED_TO_AUTHENTICATOR = 'Enter the code from your authenticator app'
export const MFA_OTP_RESENT = 'A new verification code is on its way'
// A code takes a moment to arrive, and asking for another straight away only invalidates
// the one in flight. The button stays disabled for this long after each send.
export const MFA_RESEND_COOLDOWN_SECONDS = 30
export const RESEND_CODE_IN = 'Resend code in'
// Emailed codes are not being delivered, so the factor is switched off here as well as in
// the API (MFA_EMAIL_ENABLED). Flip both together once delivery is fixed.
export const MFA_EMAIL_AVAILABLE = false
export const MFA_EMAIL_UNAVAILABLE = 'Currently unavailable.'
export const TWO_FACTOR_AUTHENTICATION_LABEL = 'Two-Factor Authentication:'
// An administrator can take the authenticator away but cannot hand it back: the secret
// only exists once the user has scanned the QR code on their own profile page.
export const MFA_AUTH_ADMIN_NOTE = 'Can only be turned off here. The user enables it from their own profile.'
export const MFA_USER_ENABLED_SUCCESS = 'Two-factor authentication updated for this user'
export const OTP_REQUIRED_MESSAGE = 'Enter the 6-digit code'
export const OTP_DIGITS_MESSAGE = 'The code is 6 digits'

export const ADMIN_NOT_ACCESSIBLE = 'This user is not able to access admin panel.'
export const NOT_FOUND_EMAIL_MESSAGE = 'No user found with this email'
export const FORGET_PASSWORD_SUCCESS = 'An email has been sent to your registered email address'

export const RESET_PASSWORD_FAILURE = 'Reset password failed'
export const SET_PASSWORD_FAILURE = 'Set password failed'
export const RESET_PASSWORD_SUCCESS = 'Password reset successfully'
export const SET_PASSWORD_SUCCESS = 'Password set successfully'
export const FOLLOW_INSTRUCTIONS = 'Please follow provided link in email to reset your password'
// export const PASSWORD_VALIDATION_MESSAGE = "Must have >=12 characters, \none uppercase, one lowercase, one number and one special case character";
export const PASSWORD_VALIDATION_MESSAGE = 'Min 12 chars, 1 number, upper, lower, and special char'
export const INVITE_NEW_USER = 'Invite new user'
export const USERS = 'Users'
export const TIME_ENTRIES_ADMIN = 'All Time Entries'
export const TIME_ENTRIES_USER = 'My Time Entries'
export const EXPENSE_ENTRIES = 'Expense Entries'
export const ADD_EXPENSE_ENTRY = 'Add Expense Entry'
export const ADD_TIME_ENTRY = 'Add Time Entry'
export const ADD_COST_ENTRY = 'Add Investigator Time Entry'
export const ADD_LEGAL_FEES = 'Add Legal Fees'
export const ADD_NON_CLIENT_ENTRY = 'Add Non Client Entry'
export const COST_ENTRIES = 'Cost Entries'
export const CATEGORIES = 'Categories'
export const FEE_TYPES = 'Fee Types'
export const DEPARTMENTS = 'Departments'
export const ADD_CATEGORY = 'Add Category'
export const ADD_FEE_TYPE = 'Add Fee Type'
export const ADD_DEPARTMENT = 'Add Department'
export const ADD_ENTRY = 'Add Entry'

/** Clock entries bill in whole 6-minute (0.1 hour) increments. The rounding
 *  itself is done server side on stopTimer — this is only here so the hint
 *  under the Duration field states the same number. */
export const TIMER_ROUNDING_INCREMENT_MINUTES = 6

export const DATE_FORMAT = 'YYYY/MM/DD'
export const TIME_WITH_SECONDS_FORMAT = 'h:mm:ss a'
export const STANDARD_DATE_FORMAT = 'MM/DD/YYYY'
export const BACKEND_DATE_FORMAT = 'YYYY-MM-DD'
export const BACKEND_DATE_TIME_FORMAT = 'YYYY-MM-DD HH:MM'
export const TIME_FORMAT = 'HH:mm'
export const USER_UPDATE_SUCCESS = 'User updated successfully'
export const USER_UNLOCK_SUCCESS = 'User unlocked successfully'
export const UNLOCK = 'Unlock'
export const LOCKED = 'Locked'
export const AT_LEAST_ONE_ROLE_REQUIRED = 'Please select at least one role'
export const INTERNAL_SERVER_ERROR = 'Internal server error'
export const USER_PASSWORD_SUCCESS = 'User password updated successfully'
export const SEARCH_PLACEHOLDER = 'Name, Email'
export const SEARCH_TEXT = 'search'
/** Minimum characters for free-text searches (2 so short client last names like "Li" or "Wu" are findable) */
export const MIN_SEARCH_LENGTH = 2
export const MIN_SEARCH_LENGTH_TEXT = `Min. ${MIN_SEARCH_LENGTH} characters`
export const CASE_SEARCH_PLACEHOLDER = 'Title/Matter#'
export const CASE_SEARCH_PLACEHOLDER_WITH_CLIENT = 'Title/Matter#/Client#'
export const CASE_SEARCH_INFO_TEXT =
  'You can search by matter title, matter #, or client first name, middle name, last name, business name'
export const PAGE_LIMIT = 10
/** Page size for entries list (fetchAllEntries); backend handles 50 with no perf concerns */
export const ENTRIES_PAGE_LIMIT = 50
/** Max page size for FetchAllCases to avoid slow requests; backend caps at 50 */
export const MAX_CASES_PAGE_LIMIT = 50
/** Page size for Account Payables list */
export const PAYABLES_PAGE_LIMIT = 25
/**
 * Page size for the Report History list. Kept separate from the global PAGE_LIMIT, which
 * is shared AppContext state used by every other paginated list.
 */
export const REPORTS_PAGE_LIMIT = 25
export const NO_USER_FOUND = 'No user found'
export const NO_CASES_FOUND = 'No Matters found'
export const NO_BILLING_TYPE_LABEL = 'No Billing Type'
export const NO_BILLING_TYPE_TOOLTIP = 'Matters with no billing type set'
export const NO_CONSULTS_FOUND = 'No Consults found'
export const USERS_TABLE_HEADERS = ['Staff', 'Initials', 'Joined on', 'Role', 'Status', 'Email verified', 'Actions']
export const CASES_TABLE_HEADERS = [
  'Matter #',
  'Advantage #',
  'Matter Title',
  'Law Type',
  'Client',
  'Paralegal',
  'Attorney',
  'Court',
  'Updated At',
  'Actions'
]
export const CLIENT_TABLE_HEADERS = [
  'First Name',
  'Middle Name',
  'Last Name',
  'Business Name',
  'Email',
  'Contract Balance',
  'Address',
  'Work Phone #',
  'Actions'
]
export const ADVERSARY_TABLE_HEADERS = ['Adversary', 'Address', 'Phone #', 'Assigned To', 'Actions']
export const JUDGE_TABLE_HEADERS = ['Judge', 'Extension', 'Phone #', 'Courts', 'Actions']
export const CONTACT_TABLE_HEADERS = ['Contact Name', 'Address', 'Phone #', 'Assigned To', 'Actions']
export const JUDGE_AGAINST_COURT_TABLE_HEADERS = ['Extension', 'Phone #', 'Actions']
export const MY_CASES_TABLE_HEADERS = [
  'Matter #',
  'Case Title',
  'Case Type',
  'Client Name',
  'Paralegal',
  'Attorney',
  'Court',
  'Updated At',
  'Actions'
]
export const CASES_UPLOAD_CSV_TABLE_HEADERS = [
  'clientid',
  'matterid',
  'name',
  'matterdesc',
  'resp_1',
  'class',
  'email',
  'address1',
  'address2',
  'city',
  'state',
  'zip',
  'phone_cell',
  'contractbalance',
  'arbalance',
  'residualbalance',
  'trustbalance',
  'lastpaydate',
  'lastpayamt'
]
export const NO_COURT_FOUND = 'No court found'
export const COURTS_TABLE_HEADERS = ['Court Name', 'County', 'Cases Assigned', 'Action']
export const LAW_TYPE_TABLE_HEADERS = ['Law Type', 'Matter Type', 'Action']
export const CUSTOM_TASKS_TABLE_HEADERS = ['Custom Task', 'Created At', 'Action']
export const CASE_TYPE_TABLE_HEADERS = ['Matter Type', 'Matters Assigned', 'Action']
export const UNABLE_TO_FETCH_ALL_CASE_TYPES = 'Unable to fetch all case types'
export const UNABLE_TO_FETCH_LAW_TYPE = 'Unable to fetch law type'
export const CASE_CREATED_SUCCESS = 'Matter created successfully'
export const Note_CREATED_SUCCESS = 'Note created successfully'
export const CASES_AGAINST_COURT_HEADER = [
  'Matter #',
  'Matter Title',
  'Matter Type',
  'Client Name',
  'Status',
  'Paralegal',
  'Attorney',
  'Court Date',
  'Updated At'
]
export const FOUR_O_FOUR = '404'
export const PAGE_NOT_FOUND = 'No Matching Data Found!'
export const LOOKS_LIKE_AN_EMPTY_SPACE =
  'Looks like an empty space. You can go back to homepage by clicking the button below'
export const PHONE_NUMBER_REGEX = /^\d{10}$/
export const PASSWORD = 'password'
export const TEXT = 'text'
export const FILE_IS_GREATER = 'File is larger than 3000000 bytes'
export const FILE_IS_LARGER = 'File is larger than 3MB'
export const NOT_APPLICABLE = 'N/A'
export const PLEASE_ENTER_VALID_PHONE_NUMBER = 'Please enter valid phone number'
export const EXTERNAL_USER_ALREADY_EXIST = 'External user already exists'
export const EMAIL_ALREADY_EXIST = 'Email already exist'
export const CLIENT_NUMBER_ALREADY_EXIST = 'Client number already exists'
export const JUVENILE = 'Juvenile'
export const DCPP = 'DCPP'
export const CSV_FORMAT_INCORRECT = 'CSV format is incorrect, please check sample CSV for reference.'
export const PHONE_FORMAT_NOT_CORRECT = 'Phone format not correct'
export const MISCELLANEOUS = 'Miscellaneous'
export const CLIENT_ADDRESS = 'Client Address'
export const ADVERSARY_ADDRESS = 'Adversary Address'
export const GENERAL_INFORMATION = 'General Information'
export const CANCEL = 'Cancel'
export const DEFAULT_MATTER_NO = '10000'
export const DEFAULT_CONSULT_MATTER_NO = 'C50000'

export const CASE_EVENT_TITLES: CaseTitle = {
  Family: ['Trial', 'CMC', 'Mesp', 'Motion', 'Mediation', 'Hearing', 'Zoom Consult', 'P / C', MISCELLANEOUS],
  Criminal: ['Trial', 'CMC', 'Mesp', 'Motion', 'Mediation', 'Hearing', 'Zoom Consult', 'P / C', MISCELLANEOUS],
  PersonalInjury: ['Depositions', 'Trial', 'Arbitration', 'SOL', 'DED', 'IME', MISCELLANEOUS],
  Immigration: [
    'Master Calendar Hearing',
    'Merits Hearing',
    'Bond Hearing',
    'I-485 Interview',
    'Naturalization Interview',
    'I-130 Interview',
    'Parole Revocation Hearing',
    'Inmate Visit',
    MISCELLANEOUS
  ],
  Civil: [
    'Statute of Limitations',
    'D.E.D.',
    'Client Meeting',
    'Motion Hearing',
    'Case Management Hearing',
    'Staus Hearing',
    'Settlement Conference',
    'Mediation',
    'Arbitration',
    'Pretrial Conference',
    'Trial',
    'Deposition',
    'Hearing',
    'Board Hearing',
    'ABC Hearing',
    'Case Review',
    'Deadline to File',
    'Deadline to File TCN',
    'Deadline to File RFA',
    'Deadline to File RFD',
    'Deadline to Answer ROGS',
    'Deadline to File Dispositive Motions',
    'Deadline to File Trio De Novo',
    'Deadline to File Appeal',
    'Deadline to File ABC Appeal',
    'Meeting',
    MISCELLANEOUS
  ],
  Misc: ['Consultation', 'Closing Date', 'Will Execution', 'POA Execution', MISCELLANEOUS],
  Municipal: ['In person Court Date', 'Zoom Court', 'Trial', MISCELLANEOUS],
  WorkmansComp: ['Workmans Comp', MISCELLANEOUS],
  Administrative: [MISCELLANEOUS],
  Appellate: [MISCELLANEOUS],
  Ethics: [MISCELLANEOUS],
  Expungements: [MISCELLANEOUS],
  Miscellaneous: [MISCELLANEOUS]
}

export const CASE_STATUS_OPTIONS = [
  { label: 'Active', value: CaseStatus.Active as string },
  { label: 'Close', value: CaseStatus.Completed as string }
]

export const PASSWORD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[\^$*.[\]{}()?\-"!@#=+%&/,><':;|_~`])\S{12,99}$/
export const TITLE_REGEX = /^\S+(?: \S+)*$/
export const SPACES_REGEX = /^\b[a-zA-Z]+\b[ ]*$/
export const phoneReg =
  /^((\\[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/
export const ALPHABETS_REGEX = /^\b(?!.*?\s{2})[A-Za-z ]{1,}\b$/
export const ALPHABETS_DOT_AND_COMMA_REGEX = /^[A-Za-z., ]*$/
export const MULTIPLE_SPACES_REGEX = /^\S+(?: \S+)*$/
export const OTP_REGEX = /^[0-9]{6}$/

export const sidebarLink = {
  dashboard: 'Dashboard',
  cases: 'Cases',
  courts: 'Courts',
  users: 'Users'
}

export const LOGIN_FIELDS = [
  {
    title: 'Email',
    fieldType: 'email',
    name: 'email'
  },
  {
    title: 'Password',
    fieldType: 'password',
    name: 'password'
  }
]

export const SET_PASSWORD_FIELDS = [
  {
    title: 'New Password',
    fieldType: 'password',
    name: 'password'
  },
  {
    title: 'Confirm Password',
    fieldType: 'password',
    name: 'repeatPassword'
  }
]

export const INVITE_USER_FORM_FIELDS = [
  {
    title: 'Email',
    fieldType: 'email',
    name: 'email'
  },
  {
    title: 'First Name',
    fieldType: 'text',
    name: 'firstName'
  },
  {
    title: 'Last Name',
    fieldType: 'text',
    name: 'lastName'
  }
]

export const GRAPHQL_QUERY_POLICY = {
  fetchPolicy: 'network-only',
  nextFetchPolicy: 'no-cache',
  notifyOnNetworkStatusChange: true
} as unknown as QueryOptions

/** Use for queries that should be cached (e.g. fetchOperatingAccount, fetchZescrowAccount, fetchAllAccounts). */
export const GRAPHQL_QUERY_POLICY_CACHE_FIRST = {
  fetchPolicy: 'cache-first' as const,
  nextFetchPolicy: 'cache-first' as const,
  notifyOnNetworkStatusChange: true
} as unknown as QueryOptions

export const COURT_EDIT_FIELDS = [
  {
    title: 'Court Name',
    fieldType: 'text',
    name: 'name'
  },
  {
    title: 'County',
    fieldType: 'text',
    name: 'county'
  }
]

export const DrawerWidthExpanded = 267
export const DrawerWidthCollapsed = 72
/** Column header for the entry's `sendNotesToMatterNotes` flag. Shared by the
 *  per-type entry table and the All Entries dashboard so the two stay in sync. */
export const SEND_NOTES_COLUMN = 'Send Notes to Matter Notes'

export const SHOW_ENTRY_HEADERS = {
  common: ['Matter ID', 'Matter', 'User', 'Actions'],
  duration: ['Date', 'Time', 'Rate', 'Amount', 'Description', 'Status'],
  expense: ['Date', 'Amount', 'Category', 'Description', 'Status'],
  legalFee: ['Date', 'Fee Type', 'Amount', 'Description', 'Status'],
  nonClient: ['User', 'Date', 'Time', 'Description']
}

export enum HELMER_KEYS {
  TIMER = 'HELMER_TIMER',
  FORM = 'HELMER_FORM'
}

export enum QUERY_PARAMS {
  MATTER = 'matter',
  STATUS = 'status',
  USER = 'user',
  FROM = 'from',
  TO = 'to',
  ALL = 'all',
  CATEGORY = 'category',
  QUERY = 'query',
  LAW_TYPES = 'lawTypes',
  CONFLICT_CHECKS = 'conflict-checks',
  SOURCE_TYPE = 'type',
  SELECTED_BILLS = 'selected-bills',
  ACTIVE_TAB = 'active-tab',
  /** Court Dates calendar: month / week / day, and the day it is centred on (YYYY-MM-DD). */
  CALENDAR_VIEW = 'view',
  CALENDAR_DATE = 'date',
  ACCOUNT_ID = 'account-id',
  PAYMENT_TAB = 'payment-tab',
  RECONCILIATION_LINE_ITEMS = 'lineItems',
  RECONCILIATION_STATUS = 'reconcileStatus',
  STATEMENT_MONTH = 'statementMonth',
  TRANSACTION_TYPE = 'transactionType',
  /** Bank reconciliation line-item listing: filter by payment method (cheque, EFT, card, …). */
  RECONCILIATION_PAYMENT_METHOD = 'paymentMethod',
  SELECTED_PAYABLES = 'selected-payables',
  ORDER_BY = 'orderBy',
  STATEMENT_BALANCE = 'statementBalance',
  CLIENT = 'client',
  PROFIT_CENTER = 'profit-center',
  VENDOR = 'vendor',
  PAYMENT_TYPE = 'payment-type',
  DATE = 'date',
  KEYWORD = 'keyword',
  /** Payments page search. Kept apart from `keyword`, which MatterDropdown writes the matter number into. */
  SEARCH = 'search',
  CREDIT_TOTAL = 'creditTotal',
  DEBIT_TOTAL = 'debitTotal',
  CURRENT_PAGE = 'currentPage',
  ORDER_DIRECTION = 'orderDir',
  ENTITY = 'entity',
  OFFICE = 'office',
  LOCATION = 'location',
  REFERENCE = 'reference',
  BILLING_TYPE = 'billingType',
  IS_WORK_COMPLETED = 'workCompleted',
  ENTRIES_SORT = 'entriesSort',
  SLIP_NUMBER = 'slipNumber',
  OFFICE_ID = 'officeId',
  PAGE_SIZE = 'pageSize',
  RECONCILED = 'reconciled',
  /** Client Ledger: the pr11 template's Entry Type filter. */
  ENTRY_TYPE = 'entryType'
}

/** Values for entries list sort (used with QUERY_PARAMS.ENTRIES_SORT) */
export const ENTRIES_SORT_VALUES = {
  MOST_RECENT: 'most_recent',
  ACTIVITY_NEWEST: 'activity_newest',
  ACTIVITY_OLDEST: 'activity_oldest'
} as const

export const CATEGORIES_LIST = ['Photocopying', 'Filing', 'Postage', 'Transcription', 'Travel', 'Other']

export const Category_Headers = ['Category', 'Notes', 'Actions']
// Both regexes below allow 0-2 colon groups, i.e. seconds are accepted:
// "2", "2:30" and "2:30:45" all pass — and the timer itself writes HH:MM:SS.
// Keep this message in step with them; it used to omit seconds and so read as
// if HH:MM:SS were invalid.
export const DURATION_FORMAT_MESSAGE = 'Duration should be in the format of HH:MM, HH:MM:SS or H.H'
export const TIME_FORMAT_HH_MM_REGEX = /^(?:0?\d|1\d|2[0-4])(?::[0-5]\d){0,2}$|^16:00$/
export const TIME_FORMAT_HH_REGEX =
  /^((?:0?\d|1\d|2[0-4])(?::[0-5]\d){1,2}|(?:0?\d|1\d|2[0-4])(?:\.\d+)?|(?:\.\d+)|16:00)$/

export enum EntryTabsLabel {
  TIME_ENTRIES = 'Time Entries',
  COST_ENTRIES = 'Investigator Time',
  EXPENSE_ENTRIES = 'Expense Entries',
  LEGAL_FEES = 'Legal Fees',
  NON_CLIENT_ENTRIES = 'Non Client Entries'
}

export enum Entry_Routes {
  TIME = 'TIME',
  COST = 'COST',
  EXPENSE = 'EXPENSE'
}

export const MATTER_LEGAL_TEAM_ROLES = [
  {
    label: 'Attorney',
    value: UserType.Attorney
  },
  {
    label: 'Paralegal',
    value: UserType.Paralegal
  },
  {
    label: 'Investigator',
    value: UserType.Investigator
  },
  {
    label: 'Supervising Attorney',
    value: UserType.SupervisingAttorney
  },
  {
    label: 'Responsible Attorney 3',
    value: UserType.ResponsibleAttorney_3
  },
  {
    label: 'Responsible Attorney 4',
    value: UserType.ResponsibleAttorney_4
  },
  {
    label: 'Responsible Attorney 5',
    value: UserType.ResponsibleAttorney_5
  },
  {
    label: 'Responsible Attorney 6',
    value: UserType.ResponsibleAttorney_6
  }
]
export const Department_Headers = ['Department', 'Department Head', 'Practice Areas', 'Description', 'Action']

export const MENU_ITEM_HEIGHT = 48
export const MENU_ITEM_PADDING_TOP = 8

export const DEFAULT_USER_ROLES = [UserRole.Attorney, UserRole.Paralegal, UserRole.Investigator]

export const FILTER_DATE_FORMAT = 'MM/DD/YYYY'

export const CREATE_CASE_STEPS = [
  { label: 'Basic Details', description: 'Add general detail about the case', heading: 'Matter Details' },
  { label: 'Matter Parties', description: 'Add clients & adversaries', heading: 'Matter Parties' },
  { label: 'Legal Team', description: 'Add lawyers & paralegals', heading: 'Legal Team' },
  { label: 'Billing', description: 'Add billing details', heading: 'Billing Details' },
  { label: 'Referrals', description: 'Add referrals for the matter', heading: 'Referrals' }
]

export type CreateCaseStepFields = { fields: (keyof CreateCaseCustomInput)[] }

// Fields should be validated on the next button click
export const stepFields: CreateCaseStepFields[] = [
  {
    fields: [
      'title',
      'docketNo',
      'lawType',
      'caseType',
      'court',
      'judgeUser',
      'judge',
      'caseNumber',
      'description',
      'openDate',
      'closeDate',
      'completedDate',
      'location',
      'profitCenter'
    ]
  },
  {
    fields: ['client', 'clientUsers', 'adversaryUsers']
  },
  {
    fields: ['legalTeam', 'consultAttorneyId']
  },
  {
    fields: ['billing', 'setting']
  },
  {
    fields: ['references']
  }
]

export type UpdateCaseStepFields = { fields: (keyof UpdateCaseCustomProps)[] }
export const UPDATE_CASE_STEP_FIELDS: UpdateCaseStepFields[] = [
  {
    fields: [
      'title',
      'docketNo',
      'lawType',
      'caseType',
      'court',
      'judgeUser',
      'judge',
      'statusType',
      'description',
      'caseNumber',
      'openDate',
      'closeDate',
      'completedDate',
      'profitCenter',
      'location'
    ]
  },
  {
    fields: ['client', 'clientUsers', 'adversaryUsers']
  },
  {
    fields: ['legalTeam']
  },
  {
    fields: ['billing', 'setting']
  },
  {
    fields: ['references']
  }
]

export const EXTERNAL_USER_CLIENT_CASE_ROLES = [
  {
    label: 'Primary Client',
    value: ExternalUserCaseRole.PrimaryClient
  },
  {
    label: 'Secondary Client',
    value: ExternalUserCaseRole.SecondaryClient
  },
  {
    label: 'Beneficiary',
    value: ExternalUserCaseRole.Beneficiary
  },
  {
    label: 'Co-signer',
    value: ExternalUserCaseRole.CoSigner
  },
  {
    label: 'Petitioner',
    value: ExternalUserCaseRole.Petitioner
  },
  {
    label: 'Witness',
    value: ExternalUserCaseRole.Witness
  },
  {
    label: 'Family Member',
    value: ExternalUserCaseRole.FamilyMember
  },
  {
    label: 'Victim',
    value: ExternalUserCaseRole.Victim
  },
  {
    label: 'Respondent',
    value: ExternalUserCaseRole.Respondent
  },
  {
    label: 'Corporate Officer',
    value: ExternalUserCaseRole.CorporateOfficer
  },
  {
    label: 'Expert',
    value: ExternalUserCaseRole.Expert
  },
  {
    label: 'Plaintiff',
    value: ExternalUserCaseRole.Plaintiff
  },
  {
    label: 'Co-Defendant',
    value: ExternalUserCaseRole.CoDefendant
  },
  {
    label: 'Friend',
    value: ExternalUserCaseRole.Friend
  },
  {
    label: 'Miscellaneous',
    value: ExternalUserCaseRole.Miscellaneous
  }
]

export const EXTERNAL_USER_ADVERSARY_CASE_ROLES = [
  {
    label: 'Primary Adversary',
    value: ExternalUserCaseRole.PrimaryAdversary
  },
  {
    label: 'Assistant Prosecutor',
    value: ExternalUserCaseRole.AssistantProsecutor
  },
  {
    label: 'Co-defendant',
    value: ExternalUserCaseRole.CoDefendant
  },
  {
    label: 'Plaintiff',
    value: ExternalUserCaseRole.Plaintiff
  },
  {
    label: 'Family Member',
    value: ExternalUserCaseRole.FamilyMember
  },
  {
    label: 'Miscellaneous',
    value: ExternalUserCaseRole.Miscellaneous
  }
]
export const Fee_Type_Headers = ['Fee Type', 'Actions']

export const ConflictTableHeaders = ['Contact', 'Matters', 'Role', 'Attorney']

export const CHARTS_OF_ACCOUNTS_HEADERS = ['Code', 'Account Name', 'Type', 'Account Number', 'Balance', 'Action']

export const ACCOUNT_CATEGORY = [
  {
    label: 'Liability',
    value: AccountCategory.Liability
  },
  {
    label: 'Asset',
    value: AccountCategory.Asset
  },
  {
    label: 'Equity',
    value: AccountCategory.Equity
  },
  {
    label: 'Revenue',
    value: AccountCategory.Revenue
  },
  {
    label: 'Expense',
    value: AccountCategory.Expense
  }
]

export const PRE_BILL_STATUSES: BillStatus[] = [
  BillStatus.Draft,
  BillStatus.SentForApproval,
  BillStatus.ApprovedByResponsibleAttorney
]

export const VOID_BILL_STATUSES: BillStatus[] = [
  BillStatus.Void,
  BillStatus.Forwarded,
  BillStatus.Archived,
  BillStatus.WrittenOff
]

export const FINAL_BILL_STATUSES: BillStatus[] = [
  BillStatus.Billed,
  BillStatus.SentToClient,
  BillStatus.PartiallyPaid,
  BillStatus.Paid,
  BillStatus.PrePaid
]

// Kept in sync with the API copy in `src/constants.ts` so the modal and any server-side rejection
// that reaches the user say exactly the same thing.
export const BILL_VOID_BLOCKED_BY_PAYMENTS_MESSAGE =
  'This bill cannot be voided because one or more payments are applied to it. ' +
  'To void this bill, delete those payments first. ' +
  'If you need further assistance, please contact support (Elmer).'

export const FINAL_FUND_REQUEST_STATUSES: BillStatus[] = [
  BillStatus.SentToClient,
  BillStatus.PartiallyPaid,
  BillStatus.Paid
]

export const Referral_Sources_Headers = ['Types', 'Action']
export const References_Headers = ['Referral Sources', 'Type', 'Active', 'Eligible for 1099 Report', 'Action']
export const isProductionMode = process.env.REACT_APP_IS_PRODUCTION_MODE === 'true'

export const MONTHLY_FLAT_FEE_TYPE = 'Monthly Flat Fee'

export const INITIAL_FLAT_FEE_TYPE = 'Initial Flat Fee Payment'

export const SPECIAL_FLAT_FEE_PAYMENT = 'Special Flat Fee Payment'

export const CONTINGENCY_AWARD_FEE_TYPE = 'Contingency Award'

export const CONSULTATION_FEE_TYPE = 'Consultation Fee'

export const ADVANCE_CONTRACT_PAYMENT = 'Advance Contract Payment'

export const Billing_Cycle_Options = [
  { value: BillingCycle.EndOfMonth, label: 'End of Month' },
  { value: BillingCycle.MidOfMonth, label: 'Mid of Month' }
  // { value: BillingCycle.CustomDate, label: 'Custom Date' }
]

export const TRANSACTION_STATUS_LABELS = {
  [TransactionStatus.AuthorizedPendingCapture]: 'Pending Capture',
  [TransactionStatus.CapturedPendingSettlement]: 'Pending Settlement',
  [TransactionStatus.CommunicationError]: 'Communication Error',
  [TransactionStatus.RefundSettledSuccessfully]: 'Refund Settled',
  [TransactionStatus.RefundPendingSettlement]: 'Refund Pending',
  [TransactionStatus.ApprovedReview]: 'Approved',
  [TransactionStatus.Declined]: 'Declined',
  [TransactionStatus.CouldNotVoid]: 'Void Error',
  [TransactionStatus.Expired]: 'Expired',
  [TransactionStatus.GeneralError]: 'General Error',
  [TransactionStatus.FailedReview]: 'Failed Review',
  [TransactionStatus.SettledSuccessfully]: 'Settled',
  [TransactionStatus.SettlementError]: 'Settlement Error',
  [TransactionStatus.UnderReview]: 'Under Review',
  [TransactionStatus.Voided]: 'Voided',
  [TransactionStatus.FdsPendingReview]: 'In Process',
  [TransactionStatus.FdsAuthorizedPendingReview]: 'Authorized Pending Review',
  [TransactionStatus.ReturnedItem]: 'Returned'
}

export const VALID_BILL_DELIVERY_STATUS = [BillStatus.SentToClient, BillStatus.PartiallyPaid, BillStatus.Paid]
export const REMINDER_BILL_ACTIONS = [
  BillTimelineAction.ReminderEmailSentToClient,
  BillTimelineAction.ReminderTextSentToClient,
  BillTimelineAction.FailedToSendReminderToClient
]
export const VALID_DELIVERY_ACTIONS = [
  ...REMINDER_BILL_ACTIONS,
  BillTimelineAction.BillInvoiceEmailSentToClient,
  BillTimelineAction.FailedToSendBillInvoiceToClient,
  BillTimelineAction.BillInvoiceTextSentToClient
]

export const BILL_DELIVERY_CARD_HEADERS = [
  { label: 'Bill #', width: '5%' },
  { label: 'Type', width: '10%' },
  { label: 'Delivery', width: '5%' },
  { label: 'Date', width: '20%' },
  { label: 'Total', width: '10%' },
  { label: 'Paid', width: '10%' },
  { label: 'Written Off', width: '10%' },
  { label: 'Balance', width: '10%' },
  { label: 'Status', width: '10%' },
  { label: 'Action', width: '5%' },
  { label: '', width: '5%' }
]

export const CLIENT_PAYMENT_HEADERS = [
  'Pay Date',
  'Logged Date',
  'Bill#',
  'Matter#',
  'Client',
  'Source',
  'Destination',
  'Payment',
  'Reference ID',
  'Slip #',
  'Location',
  'Notes',
  'Rec?',
  'Actions'
]

export const VENDOR_PAYMENT_HEADERS = [
  'Pay Date',
  'Logged Date',
  'Vendor',
  'Payment Type',
  'Payment Method',
  'Account',
  'Payment',
  'Reference ID',
  'Location',
  'Notes',
  'Rec?',
  'Actions'
]

export const FIRM_PAYMENT_HEADERS = [
  'Pay Date',
  'Logged Date',
  'Vendor',
  'Matter#',
  'Payment Type',
  'Payment Method',
  'Destination',
  'Payment',
  'Reference ID',
  'Location',
  'Notes',
  'Rec?',
  'Actions'
]

export const VENDOR_HEADERS = [
  'Vendor #',
  'Name/Business Name',
  'Phone',
  'Address',
  'Profit Center',
  '1099 Required',
  'Actions'
]
export const OFFICE_HEADERS = ['Profit Center #', 'Name', 'Phone', 'Address', 'Active Location', 'Actions']
export const BANK_RECONCILIATION_HEADERS = [
  '',
  'Check No',
  'Date',
  'Amount',
  'Type',
  'Slip #',
  'Payment Method',
  'Client/Vendor',
  'Location'
]

export const ACCOUNT_PAYABLES_HEADERS = [
  'Voucher #',
  'Vendors',
  'Matter',
  'Pay Date',
  'Invoice Date',
  'Payment Method',
  'Check / Ref No',
  'Bank Account',
  'GL Account',
  'Status',
  'Printed At',
  'Vch Total $',
  'Reconciled',
  'Actions'
]

export const PAYABLE_ENTRY_PAYMENT_METHODS = [
  PaymentMethod.Cheque,
  PaymentMethod.Card,
  PaymentMethod.Cash,
  PaymentMethod.Ach,
  PaymentMethod.Eft,
  PaymentMethod.Other
]

export const ALL_PAYMENT_METHODS: PaymentMethod[] = [...Object.values(PaymentMethod)]

export type SORT_DIR_TYPE = 'ASC' | 'DESC'

/**
 * Entry Type options for the Client Ledger screen.
 *
 * Must stay in step with the pr11 report template's `entryType` options
 * (api: `reports/templates/index.ts`) — the API rejects anything it does not declare, so a value
 * added here alone is a 400, not a silently wider filter.
 */
export const CLIENT_LEDGER_ENTRY_TYPES = [
  'Time Entry',
  'Cost Charged',
  'Expense Charged',
  'Cost/Expense Charged',
  'Fee Charged',
  'Interest Charged',
  'Payment Received',
  'Trust Deposit',
  'Trust Check'
]

export const GENERAL_LEDGER_HEADERS = [
  'Account',
  'Date',
  'Payment Type',
  'Amount',
  'Matter',
  'Bill',
  'Payable',
  'Description'
]

export const INTEREST_CONFIGURATION = [
  {
    rate: 5,
    applicableDays: 45
  },
  {
    rate: 16
  }
]

export const SEARCHABLE_USER_ROLES: UserRole[] = [
  UserRole.Admin,
  UserRole.Attorney,
  UserRole.Paralegal,
  UserRole.Investigator,
  UserRole.Accountant,
  UserRole.Staff
]
const HAWAII_STATE_NAME = 'Hawaii'
export const US_STATES = [...statesUs.filter(s => s.contiguous || s.name === HAWAII_STATE_NAME)]
export const ATTORNEY_USER_TYPES = [
  UserType.AssignedTo,
  UserType.OriginatorAttorney,
  UserType.ResponsibleAttorney_1,
  UserType.ResponsibleAttorney_2,
  UserType.ResponsibleAttorney_3,
  UserType.ResponsibleAttorney_4,
  UserType.ResponsibleAttorney_5,
  UserType.ResponsibleAttorney_6
]

export const FLAT_FEE_TOOLTIPS = {
  contractFee: 'The total agreed flat fee for this matter, before any discount is applied.',
  initialPayment: 'The upfront portion of the contract fee due at the start of the engagement.',
  monthlyPayment: 'The recurring monthly installment billed toward the contract fee.',
  totalDiscount:
    'The reduction applied to the contract fee. Paid, due, and residual amounts are all calculated from the discounted contract fee.',
  contractFeePaid:
    'The portion of the discounted contract fee that has been paid to date. It includes unapplied credits on the matter, which are applied to the next bill.',
  contractFeeDue: 'The portion of the discounted contract fee that has already been billed but is still unpaid.',
  residualBalance: 'The portion of the discounted contract fee that has not yet been billed to the client.',
  paidToDate: 'The net amount of fees and costs paid on this matter to date.',
  payoffAmount:
    'The total needed to close out the contract today: the unbilled remainder of the discounted contract fee, plus any outstanding balance on issued bills (including interest), minus any client credit on account.',
  hybridFlatFeePaid: 'The portion of the flat fee that has been paid to date.',
  hybridFlatFeeDue: 'The portion of the flat fee that has been billed but is still unpaid.',
  hybridFlatFeeConsumed:
    'The amount of hourly work logged that has been drawn against the flat fee, capped at the flat fee amount.'
}

// APP routes

export const ROOT_ROUTE = '/'
export const DASHBOARD_ROUTE = '/dashboard'

// matter routes
export const CASE_ROUTE = '/matters'
export const CASE_SEARCH_ROUTE = `${CASE_ROUTE}/search`
export const CASE_DETAIL_ROUTE = `${CASE_ROUTE}/matter-detail`
export const CASE_DETAIL_ROUTE_WITH_ID = `${CASE_DETAIL_ROUTE}/:id`
// Duplicate: the create form pre-filled from matter `:id`
export const DUPLICATE_CASE_ROUTE = `${CASE_ROUTE}/duplicate`
export const DUPLICATE_CASE_ROUTE_WITH_ID = `${DUPLICATE_CASE_ROUTE}/:id`

// consult routes
export const CONSULT_ROUTE = '/consults'
export const DUPLICATE_CONSULT_ROUTE = `${CONSULT_ROUTE}/duplicate`
export const DUPLICATE_CONSULT_ROUTE_WITH_ID = `${DUPLICATE_CONSULT_ROUTE}/:id`

export const FIRM_DASHBOARD_ROUTE = '/firm-dashboard'
export const COURT_DATES_ROUTE = '/court-dates'

// accounting routes
export const ACCOUNTS_ROUTE = '/accounts'
export const PAYMENTS_ROUTE = `${ACCOUNTS_ROUTE}/payments`

// client-facing bill, opened from the emailed link without signing in
export const BILL_VIEW_ROUTE = '/bill/view'
export const BILL_VIEW_ROUTE_WITH_TOKEN = `${BILL_VIEW_ROUTE}/:token`

export const DASHBOARD_LINK = [
  { title: sidebarLink.dashboard, link: DASHBOARD_ROUTE },
  { title: sidebarLink.cases, link: CASE_ROUTE },
  { title: sidebarLink.courts, link: '/courts' }
]

export const AUTH_LINKS = {
  FORGET_PASSWORD_LINK: '/forgot-password',
  LOGIN_LINK: '/login',
  SET_PASSWORD: '/set-password',
  RESET_PASSWORD: '/reset-password',
  DASHBOARD_LINK: DASHBOARD_ROUTE
}
